import LoadingDots from 'components/LoadingDots';
import Popover from 'components/Popover';
import { getMoreFilters } from 'data/filters';
import { Aggregations } from 'model/SearchResults';
import FiltersBlock from 'modules/common/Popovers/MoreFiltersPopover/FiltersBlock';
import KeywordSearch from 'modules/common/Popovers/MoreFiltersPopover/KeywordSearch';
import {
  ActiveFilters,
  FilterActions,
} from 'modules/common/Popovers/MoreFiltersPopover/hooks/useMoreFilters';
import PopoverFooter from 'modules/common/Popovers/PopoverFooter';
import { PopoverTriggerSource } from 'modules/common/SubBars/ExperienceFilterSubNavbar/usePopoverTrigger';
import React, { useEffect } from 'react';

import TopFilters from './TopFilters';

const CSS = require('./MoreFiltersPopover.module.scss');

interface MoreFiltersPopoverProps {
  aggregations: Aggregations;
  onApply: (source: PopoverTriggerSource) => (e: any) => void;
  onClear: () => void;
  amountOfResults: number;
  resultsLoading: boolean;
  activeFilters: ActiveFilters;
  actions: FilterActions;
}

function MoreFiltersPopover({
  onApply,
  onClear,
  amountOfResults,
  resultsLoading,
  aggregations,
  activeFilters,
  actions,
}: MoreFiltersPopoverProps) {
  const filters = getMoreFilters(aggregations);

  useEffect(() => {
    function onKeyDown(e) {
      // @ts-ignore
      if (e?.key === 'Enter' && !(document.activeElement?.value ?? '')) {
        onApply('enter button')(e);
      }
    }

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Popover
      show
      name="moreFilterPopover"
      underNavbar
      className={CSS.Popover}
      onClose={onApply('escape button')}
      onOutSideClick={onApply('outside click')}
    >
      <div className={CSS.MoreFilters}>
        <div className={CSS.MoreFiltersContent}>
          <div className={CSS.TopRow}>
            <KeywordSearch
              keywords={activeFilters.keywords}
              onKeywordAdd={actions.onKeywordAdd}
              onKeywordRemove={actions.onKeywordRemove}
            />
            <TopFilters
              selectedCategories={activeFilters.topFilterOptions}
              onCategoryToggle={actions.onToggleTopFilter}
            />
          </div>
          <div className={CSS.CategoriesContainer}>
            {filters && (
              <>
                {filters.toursSubcategoriesFilters.length > 0 && (
                  <FiltersBlock
                    onMainFilterChange={actions.onToggleAllTours}
                    onSubFilterChange={actions.onToggleTours}
                    mainFilter={filters.toursFilter}
                    subFilters={filters.toursSubcategoriesFilters}
                    isMainFilterSelected={activeFilters.toursFilterStatus}
                    selectedSubFilters={activeFilters.toursOptions}
                  />
                )}
                {filters.activitiesSubcategoriesFilters.length > 0 && (
                  <FiltersBlock
                    onMainFilterChange={actions.onToggleAllActivities}
                    onSubFilterChange={actions.onToggleActivities}
                    mainFilter={filters.activitiesFilter}
                    subFilters={filters.activitiesSubcategoriesFilters}
                    isMainFilterSelected={activeFilters.activitiesFilterStatus}
                    selectedSubFilters={activeFilters.activitiesOptions}
                  />
                )}
                <FiltersBlock
                  mainFilter={filters.transportMode}
                  subFilters={filters.transportModeSubOptions}
                  onSubFilterChange={actions.onToggleTransportMode}
                  selectedSubFilters={activeFilters.transportModeOptions}
                />
                <FiltersBlock
                  mainFilter={filters.durationFilter}
                  subFilters={filters.durationSubOptions}
                  onSubFilterChange={actions.onToggleDuration}
                  selectedSubFilters={activeFilters.durationOptions}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <PopoverFooter
        onClear={onClear}
        onApply={onApply('apply button')}
        extraContent={
          resultsLoading ? (
            <LoadingDots size="s" />
          ) : (
            `Explore ${amountOfResults} tours and activities`
          )
        }
      />
    </Popover>
  );
}

export default MoreFiltersPopover;
